import type { ApiConfig } from '@api/types';
import { typedMethodPost, typedMethodGet } from '@api/utils';
import type {
  SearchUsersRequestDto,
  DeleteUserRequestDto,
  InviteUserRequestDto,
  UpdateUserProfileRequestDto,
  DeviceAliveEventRequestDto,
  RemoveTenantRequestDto,
  ResetConnectorRequestDto,
  ChangeAccessRequestDto,
  ConfigureIpRangeForScanRequestDto,
  RemoveIpRangeFromScanRequestDto,
  RequestForIpRangeScanRequestDto,
  ValidateOnboardingRequestDto,
  OnboardConnectorRequestDto,
  UpdateImnooApiKeyRequestDto,
  GenerateApiKeyRequestDto,
  OnboardProductionSourceSystemRequestDto,
  ConfigureNetworkScanRequestDto,
  OnboardingWizardSubmitRequestDto,
} from '../../generated/models';
import type {
  SearchUsersResponse,
  DeleteUserResponse,
  GetConnectorHealthResponse,
  DeviceAliveEventResponse,
  RemoveTenantResponse,
  ChangeAccessResponse,
  ValidateOnboardingResponse,
  ConnectorProductionConfigurationDetailsResponse,
  ConnectorIpRange,
  ConnectorIpScanStatus,
  Timezone,
  Country,
  ApiKey,
  ImnooApiKey,
  UpdateImnooApiKeyResponse,
  GenerateApiKeyResponse,
  ErpSourceSystemType,
  CheckApiResponse,
} from '../../domain/types';
import {
  toSearchUsersResponse,
  toDeleteUserResponse,
  toGetConnectorHealthResponse,
  toDeviceAliveEventResponse,
  toRemoveTenantResponse,
  toChangeAccessResponse,
  toValidateOnboardingResponse,
  toConnectorProductionConfigurationDetailsResponse,
  toConnectorIpRanges,
  toConnectorIpScanStatuses,
  toTimezones,
  toCountries,
  toApiKeys,
  toImnooApiKeys,
  toUpdateImnooApiKeyResponse,
  toGenerateApiKeyResponse,
  toErpSourceSystemTypes,
  toCheckApiResponse,
} from '../../domain/mappers';
import type { ErpSourceSystemTypesParams } from './types';

export default ({ baseURL, baseURLoData }: ApiConfig) => ({
  checkApi: typedMethodPost<void, CheckApiResponse>(
    (data) =>
      fetch(`${baseURL}/v2/membership/checkApi`, {
        method: 'POST',
        ...data,
      }),
    toCheckApiResponse
  ),
  searchUsers: typedMethodPost<SearchUsersRequestDto, SearchUsersResponse>(
    (data) =>
      fetch(`${baseURL}/v1/membership/searchUsers`, {
        method: 'POST',
        ...data,
      }),
    toSearchUsersResponse
  ),
  deleteUser: typedMethodPost<DeleteUserRequestDto, DeleteUserResponse>(
    (data) =>
      fetch(`${baseURL}/v1/membership/deleteUser`, {
        method: 'POST',
        ...data,
      }),
    toDeleteUserResponse
  ),
  inviteUser: typedMethodPost<InviteUserRequestDto, void>(
    (data) =>
      fetch(`${baseURL}/v1/membership/inviteUser`, {
        method: 'POST',
        ...data,
      })
  ),
  updateUserProfile: typedMethodPost<UpdateUserProfileRequestDto, void>(
    (data) =>
      fetch(`${baseURL}/v1/membership/updateUserProfile`, {
        method: 'POST',
        ...data,
      })
  ),
  getConnectorHealth: typedMethodPost<void, GetConnectorHealthResponse>(
    (data) =>
      fetch(`${baseURL}/v2/membership/GetConnectorHealth`, {
        method: 'POST',
        ...data,
      }),
    toGetConnectorHealthResponse
  ),
  deviceAliveEvent: typedMethodPost<DeviceAliveEventRequestDto, DeviceAliveEventResponse>(
    (data) =>
      fetch(`${baseURL}/v1/membership/deviceAliveEvent`, {
        method: 'POST',
        ...data,
      }),
    toDeviceAliveEventResponse
  ),
  removeTenant: typedMethodPost<RemoveTenantRequestDto, RemoveTenantResponse>(
    (data) =>
      fetch(`${baseURL}/v1/membership/removeTenant`, {
        method: 'POST',
        ...data,
      }),
    toRemoveTenantResponse
  ),
  resetConnector: typedMethodPost<ResetConnectorRequestDto, void>(
    (data) =>
      fetch(`${baseURL}/v1/membership/resetConnector`, {
        method: 'POST',
        ...data,
        body: JSON.stringify({}),
      })
  ),
  changeAccess: typedMethodPost<ChangeAccessRequestDto, ChangeAccessResponse>(
    (data) =>
      fetch(`${baseURL}/v1/membership/changeAccess`, {
        method: 'POST',
        ...data,
      }),
    toChangeAccessResponse
  ),
  configureIpRangeForScan: typedMethodPost<ConfigureIpRangeForScanRequestDto, void>(
    (data) =>
      fetch(`${baseURL}/v1/membership/configureIpRangeForScan`, {
        method: 'POST',
        ...data,
      })
  ),
  removeIpRangeFromScan: typedMethodPost<RemoveIpRangeFromScanRequestDto, void>(
    (data) =>
      fetch(`${baseURL}/v1/membership/removeIpRangeFromScan`, {
        method: 'POST',
        ...data,
      })
  ),
  requestIpRangeScan: typedMethodPost<RequestForIpRangeScanRequestDto, void>(
    (data) =>
      fetch(`${baseURL}/v1/membership/requestIpRangeScan`, {
        method: 'POST',
        ...data,
      })
  ),
  validateOnboarding: typedMethodPost<ValidateOnboardingRequestDto, ValidateOnboardingResponse>(
    (data) =>
      fetch(`${baseURL}/v1/membership/validateOnboarding`, {
        method: 'POST',
        ...data,
      }),
    toValidateOnboardingResponse
  ),
  validateOnboardingAnonym: typedMethodPost<ValidateOnboardingRequestDto, ValidateOnboardingResponse>(
    (data) =>
      fetch(`${baseURL}/v1/membershipOnboarding/validateOnboarding`, {
        method: 'POST',
        ...data,
      }),
    toValidateOnboardingResponse
  ),
  onboardConnector: typedMethodPost<OnboardConnectorRequestDto, void>(
    (data) =>
      fetch(`${baseURL}/v1/membership/onboardConnector`, {
        method: 'POST',
        ...data,
      })
  ),
  onboardConnectorAnonym: typedMethodPost<OnboardConnectorRequestDto, void>(
    (data) =>
      fetch(`${baseURL}/v1/membershipOnboarding/onboardConnector`, {
        method: 'POST',
        ...data,
      })
  ),
  connectorProductionConfigurationDetails: typedMethodPost<void, ConnectorProductionConfigurationDetailsResponse>(
    (data) =>
      fetch(`${baseURL}/v2/membership/connectorProductionConfigurationDetails`, {
        method: 'POST',
        ...data,
      }),
    toConnectorProductionConfigurationDetailsResponse
  ),
  connectorIpRange: typedMethodGet<void, ConnectorIpRange[]>(
    (data) =>
      fetch(`${baseURLoData}/odata/ConnectorIpRange`, {
        method: 'GET',
        headers: data.headers,
      }),
    toConnectorIpRanges
  ),
  connectorIpScanStatus: typedMethodGet<void, ConnectorIpScanStatus[]>(
    (data) =>
      fetch(`${baseURLoData}/odata/ConnectorIpScanStatus`, {
        method: 'GET',
        headers: data.headers,
      }),
    toConnectorIpScanStatuses
  ),
  getTimezone: typedMethodGet<void, Timezone[]>(
    (data) =>
      fetch(`${baseURLoData}/odata/Timezone`, {
        method: 'GET',
        headers: data.headers,
      }),
    toTimezones
  ),
  getCountryCodes: typedMethodGet<void, Country[]>(
    (data) =>
      fetch(`${baseURLoData}/odata/Country`, {
        method: 'GET',
        headers: data.headers,
      }),
    toCountries
  ),
  getApiKeys: typedMethodGet<void, ApiKey[]>(
    (data) =>
      fetch(`${baseURLoData}/odata/apiKey`, {
        method: 'GET',
        headers: data.headers,
      }),
    toApiKeys
  ),
  getImnooApiKey: typedMethodGet<void, ImnooApiKey[]>(
    (data) =>
      fetch(`${baseURLoData}/odata/imnooApiKey`, {
        method: 'GET',
        headers: data.headers,
      }),
    toImnooApiKeys
  ),
  updateImnooApiKey: typedMethodPost<UpdateImnooApiKeyRequestDto, UpdateImnooApiKeyResponse>(
    (data) =>
      fetch(`${baseURL}/v1/membership/updateImnooApiKey`, {
        method: 'POST',
        ...data,
      }),
    toUpdateImnooApiKeyResponse
  ),
  generateApiKeys: typedMethodPost<GenerateApiKeyRequestDto, GenerateApiKeyResponse>(
    (data) =>
      fetch(`${baseURL}/v1/membership/generateApiKey`, {
        method: 'POST',
        ...data,
      }),
    toGenerateApiKeyResponse
  ),
  onboardProductionSourceSystem: typedMethodPost<OnboardProductionSourceSystemRequestDto, void>(
    (data) =>
      fetch(`${baseURL}/v2/membership/onboardProductionSourceSystem`, {
        method: 'POST',
        ...data,
      })
  ),
  configureNetworkScan: typedMethodPost<ConfigureNetworkScanRequestDto, void>(
    (data) =>
      fetch(`${baseURL}/v1/membership/configureNetworkScan`, {
        method: 'POST',
        ...data,
      })
  ),
  erpSourceSystemTypes: typedMethodGet<ErpSourceSystemTypesParams, ErpSourceSystemType[]>(
    (data) =>
      fetch(`${baseURLoData}/odata/v2/ErpSourceSystemTypes${data.params?.filter ? data.params.filter : ''}`, {
        method: 'GET',
        headers: data.headers,
      }),
    toErpSourceSystemTypes
  ),
  submitOnboardingWizard: typedMethodPost<OnboardingWizardSubmitRequestDto, void>(
    (data) =>
      fetch(`${baseURL}/v2/membership/submitOnboardingWizardDetails`, {
        method: 'POST',
        ...data,
      })
  ),
});

// Re-export domain types for consumers
export type {
  CheckApiResponse,
  SearchUsersResponse,
  DeleteUserResponse,
  GetConnectorHealthResponse,
  DeviceAliveEventResponse,
  RemoveTenantResponse,
  ChangeAccessResponse,
  ValidateOnboardingResponse,
  ConnectorProductionConfigurationDetailsResponse,
  ConnectorIpRange,
  ConnectorIpScanStatus,
  Timezone,
  Country,
  ApiKey,
  ImnooApiKey,
  UpdateImnooApiKeyResponse,
  GenerateApiKeyResponse,
  ErpSourceSystemType,
  OnboardingWizardSubmitRequest,
  OnboardingWizardRole,
} from '../../domain/types';

// Re-export request DTOs (still needed for building requests)
export type {
  SearchUsersRequestDto,
  DeleteUserRequestDto,
  InviteUserRequestDto,
  UpdateUserProfileRequestDto,
  DeviceAliveEventRequestDto,
  RemoveTenantRequestDto,
  ResetConnectorRequestDto,
  ChangeAccessRequestDto,
  ConfigureIpRangeForScanRequestDto,
  RemoveIpRangeFromScanRequestDto,
  RequestForIpRangeScanRequestDto,
  ValidateOnboardingRequestDto,
  OnboardConnectorRequestDto,
  UpdateImnooApiKeyRequestDto,
  GenerateApiKeyRequestDto,
  OnboardProductionSourceSystemRequestDto,
  ConfigureNetworkScanRequestDto,
  OnboardingWizardSubmitRequestDto,
  OnboardingWizardRoleDto,
} from '../../generated/models';

export type { ErpSourceSystemTypesParams } from './types';
